import { plural } from '@lingui/core/macro';
import { Plural, Trans } from '@lingui/react/macro';

import { useAttributionKpiMetrics } from '@/api/attribution';
import { KpiMetric, KpiMetricGroup } from '@/components/kpi';
import { usePageFilterContext } from '@/components/page';
import { numberFormat } from '@/helper/numberFormatter';

const AttributionKPIMetrics = () => {
  const {
    pageFilters: { startDate, endDate, audience },
  } = usePageFilterContext();
  const { attributionKpi, isLoading, error } = useAttributionKpiMetrics(
    startDate,
    endDate,
    audience,
  );

  return (
    <KpiMetricGroup>
      <KpiMetric
        label={<Trans>Pipeline Opportunities</Trans>}
        value={
          attributionKpi &&
          numberFormat(attributionKpi.pipelineOpportunityRevenue, {
            isCurrency: true,
            precision: 0,
          })
        }
        caption={
          attributionKpi?.pipelineOpportunityCount && (
            <Plural
              value={attributionKpi && numberFormat(attributionKpi.pipelineOpportunityCount)}
              one="# Opportunity"
              other="# Opportunities"
            />
          )
        }
        infoPopover={{
          body: (
            <Trans>
              Opportunities with a create date within the dates selected and their total potential
              revenue amount.
            </Trans>
          ),
        }}
        isLoading={isLoading}
        error={error}
      />
      <KpiMetric
        label={<Trans>Closed Won Opportunities</Trans>}
        value={
          attributionKpi &&
          numberFormat(attributionKpi.closedWonRevenue, {
            isCurrency: true,
            precision: 0,
          })
        }
        caption={
          attributionKpi?.closedWonOpportunityCount && (
            <Plural
              value={attributionKpi && numberFormat(attributionKpi.closedWonOpportunityCount)}
              one="# Opportunity"
              other="# Opportunities"
            />
          )
        }
        infoPopover={{
          body: (
            <Trans>
              Opportunities with a close date within the dates selected and their total revenue
              amount.
            </Trans>
          ),
        }}
        isLoading={isLoading}
        error={error}
      />
      <KpiMetric
        label={<Trans>Opps to Closed Won</Trans>}
        value={
          attributionKpi && numberFormat(attributionKpi.pipelineToClosedWonRatio, { precision: 1 })
        }
        infoPopover={{
          body: (
            <Trans>
              Ratio of opportunities created to opportunities closed won within the selected date
              range.
            </Trans>
          ),
        }}
        isLoading={isLoading}
        error={error}
      />
      <KpiMetric
        label={<Trans>Win Rate</Trans>}
        value={
          attributionKpi && numberFormat(attributionKpi.winRate, { isPercent: true, precision: 1 })
        }
        caption={
          attributionKpi?.winRate && (
            <span>
              <Trans>
                {attributionKpi?.closedWonOpportunityCount} Closed Won /{' '}
                <Plural
                  value={
                    attributionKpi &&
                    numberFormat(
                      attributionKpi.closedWonOpportunityCount +
                        attributionKpi.closedLostOpportunityCount,
                    )
                  }
                  one="# Closed Opp"
                  other="# Closed Opps"
                />
              </Trans>
            </span>
          )
        }
        infoPopover={{
          body: (
            <Trans>
              Percentage of closed opportunities that were won within the selected date range.
            </Trans>
          ),
        }}
        isLoading={isLoading}
        error={error}
      />
      <KpiMetric
        label={<Trans>Avg. Deal Cycle Length</Trans>}
        value={
          attributionKpi &&
          plural(numberFormat(attributionKpi.averageDealCycleDays), {
            one: '# Day',
            other: '# Days',
          })
        }
        infoPopover={{
          body: (
            <Trans>
              Average total days from the first interaction to closed won date. This includes the
              influence window so it is usually longer than the opportunity open to close timeline.
            </Trans>
          ),
        }}
        isLoading={isLoading}
        error={error}
      />
    </KpiMetricGroup>
  );
};

export default AttributionKPIMetrics;
